// ============================================================
//  Identitätsauflösung (Umsetzung §7.2). Konservativ:
//  fehlender oder mehrdeutiger Schlüssel ⇒ KEINE Zusammenführung (I13),
//  Kollisionen werden ausgewiesen (I14). Schlüsselwechsel rechnet alles
//  neu (I12) — Personenzahl UND FUE ändern sich.
// ============================================================

import type { Account, Collision, IdentityKey, IdentityResolution, LicenseClass, Person } from "./types";
import { CLASS_ORDER, DEFAULT_RATIO, type RatioTable, weight } from "./rules";

function emptyByClass(): Record<LicenseClass, { persons: number; fue: number }> {
  const out = {} as Record<LicenseClass, { persons: number; fue: number }>;
  for (const c of CLASS_ORDER) out[c] = { persons: 0, fue: 0 };
  return out;
}

export function normalize(key: IdentityKey, raw?: string): string | null {
  if (raw === undefined || raw === null) return null;
  const s = raw.trim();
  if (s === "") return null;
  switch (key) {
    case "personalnummer":
      return s.replace(/^0+/, "") || "0"; // führende Nullen entfernen
    case "email":
      return s.toLowerCase();
    case "username":
    case "custom":
    default:
      return s.toUpperCase();
  }
}

function keyRaw(account: Account, key: IdentityKey): string | undefined {
  switch (key) {
    case "personalnummer":
      return account.personalnummer;
    case "email":
      return account.email;
    case "username":
    case "custom":
    default:
      return account.username;
  }
}

/**
 * Löst Konten zu Personen auf — nur die fachlichen Konten (personId "P-…"),
 * technische User bleiben außen vor (I7).
 */
export function resolveIdentity(
  accounts: Account[],
  persons: Person[],
  key: IdentityKey,
  ratio: RatioTable = DEFAULT_RATIO,
): IdentityResolution {
  const personWeight = new Map<string, number>();
  const personClass = new Map<string, LicenseClass>();
  for (const p of persons) {
    personWeight.set(p.id, weight(p.licenseClass, ratio));
    personClass.set(p.id, p.licenseClass);
  }

  const groups = new Map<string, Account[]>();
  let unresolvedAccounts = 0;
  let fueTotal = 0;
  let recognizedPersons = 0;
  const distinctPersons = new Set<string>();
  const byClass = emptyByClass();

  // Zählt eine erkannte Einheit (ein Abrechnungssubjekt) auf Person p.
  const addUnit = (personId: string) => {
    recognizedPersons++;
    const w = personWeight.get(personId) ?? 0;
    fueTotal += w;
    const cls = personClass.get(personId);
    if (cls) {
      byClass[cls].persons++;
      byClass[cls].fue += w;
    }
  };

  // Gruppieren; fehlender Schlüssel ⇒ eigene Einheit (nicht zusammenführen)
  for (const a of accounts) {
    if (!a.personId.startsWith("P-")) continue; // technische User ausschließen
    distinctPersons.add(a.personId);
    const norm = normalize(key, keyRaw(a, key));
    if (norm === null) {
      // fehlender Schlüssel ⇒ zählt einzeln (I13)
      unresolvedAccounts++;
      addUnit(a.personId);
      continue;
    }
    const arr = groups.get(norm);
    if (arr) arr.push(a);
    else groups.set(norm, [a]);
  }

  const collisions: Collision[] = [];
  for (const [k, arr] of groups) {
    const persIds = new Set(arr.map((a) => a.personId));
    if (persIds.size > 1) {
      // mehrdeutig ⇒ KEINE Zusammenführung, jedes Konto zählt einzeln (I13/I14)
      for (const a of arr) addUnit(a.personId);
      if (collisions.length < 200) {
        collisions.push({
          key: k,
          accounts: arr.map((a) => a.id),
          conflict: `Gleicher ${key} für ${persIds.size} verschiedene Personen (${[...persIds].slice(0, 3).join(", ")}…)`,
        });
      }
    } else {
      // eindeutige Gruppe ⇒ eine Person
      addUnit(arr[0].personId);
    }
  }

  // Personen, die in mehr als einer Einheit auftauchen ⇒ würden doppelt lizenziert.
  const doubleLicensed = recognizedPersons - distinctPersons.size;

  return {
    key,
    recognizedPersons,
    unresolvedAccounts,
    collisions,
    fueTotal,
    doubleLicensed: Math.max(0, doubleLicensed),
    byClass,
  };
}
